import type { ApiClient } from '@nly/api-client'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apiClient } from '@/api'
import { formatApiError } from '@/utils/apiError'

type MedicalInstitutionListResult = Awaited<ReturnType<ApiClient['medicalInstitution']['list']>>
export type MedicalInstitution = MedicalInstitutionListResult['items'][number]

export const useMedicalInstitutionStore = defineStore('medicalInstitution', () => {
  const snapshot = ref<MedicalInstitutionListResult | null>(null)
  const loading = ref(false)
  const mutationLoading = ref(false)
  const error = ref<string | null>(null)
  let readGeneration = 0

  const items = computed(() => snapshot.value?.items ?? [])

  async function load() {
    const request = ++readGeneration
    loading.value = true
    error.value = null
    try {
      const result = await apiClient.medicalInstitution.list()
      if (request !== readGeneration) {
        return result
      }
      snapshot.value = result
      return result
    }
    catch (cause) {
      if (request === readGeneration) {
        error.value = formatApiError(cause, '医疗机构列表加载失败')
      }
      throw cause
    }
    finally {
      if (request === readGeneration) {
        loading.value = false
      }
    }
  }

  async function create(input: { name: string, regionCode?: string | null, directorName?: string | null, phone?: string | null }) {
    mutationLoading.value = true
    try {
      return await apiClient.medicalInstitution.create(input)
    }
    catch (cause) {
      error.value = formatApiError(cause, '医疗机构保存失败')
      throw cause
    }
    finally {
      mutationLoading.value = false
    }
  }

  function clearError() {
    error.value = null
  }

  function reset() {
    readGeneration++
    snapshot.value = null
    loading.value = false
    mutationLoading.value = false
    error.value = null
  }

  return {
    items,
    loading: computed(() => loading.value),
    mutationLoading: computed(() => mutationLoading.value),
    error: computed(() => error.value),
    load,
    create,
    clearError,
    reset,
  }
})
